import { useEffect, useState } from "react";
import { dl } from "@/lib/docstate";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, Heart, Menu, X } from "lucide-react";
import { useDocstate } from "@/context/DocstateContext";
import { useShortlist } from "@/context/ShortlistContext";

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

const NAV = [
  { label: "Buy", to: dl("/buy") },
  { label: "Rent", to: dl("/rent") },
  { label: "Off-Plan", to: dl("/off-plan") },
  { label: "Commercial", to: dl("/commercial") },
  { label: "Holiday Homes", to: dl("/holiday-homes") },
  { label: "Services", children: [{ label: "Sell", to: dl("/sell") }, { label: "Property Management", to: dl("/property-management") }] },
  { label: "About", children: [{ label: "About Us", to: dl("/about") }, { label: "Meet the Team", to: dl("/team") }, { label: "Careers", to: dl("/careers") }] },
  { label: "Insights", children: [{ label: "Blog", to: dl("/blog") }, { label: "Area Guides", to: dl("/area-guides") }, { label: "Videos", to: dl("/videos") }, { label: "Podcasts", to: dl("/podcasts") }] },
  { label: "Contact", to: dl("/contact") },
];

export const DocstateHeader = () => {
  const data = useDocstate();
  const s = data?.settings || {};
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => { setOpen(false); }, [location]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const { items } = useShortlist();
  const light = (s.theme || "dark") === "light";
  const logo = light ? (s.logo_url || s.logo_dark_url) : (s.logo_dark_url || s.logo_url);
  const solid = scrolled || open;
  const headerBg = solid
    ? (light ? "border-b border-black/10 bg-white/92 backdrop-blur-2xl" : "border-b border-[#C9A24B]/20 bg-[#0B0B0C]/92 backdrop-blur-2xl")
    : (light ? "bg-gradient-to-b from-white/85 to-transparent" : "bg-gradient-to-b from-black/60 to-transparent");

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${headerBg}`}
      data-testid="docstate-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-6 px-4 sm:px-8 lg:px-12">
        <Link to={dl()} className="flex shrink-0 items-center gap-2" data-testid="docstate-header-logo">
          {logo ? <img src={logo} alt="Docstate Properties" className="h-9 w-auto object-contain" />
            : <><img src="/assets/docstate-mark.png" alt="" aria-hidden className="h-8 w-8 rounded-md object-contain" /><span className={`ds-display text-xl font-semibold tracking-wide ${light ? "text-[#1A1615]" : "text-white"}`}>DOCSTATE</span></>}
        </Link>

        <nav className="hidden flex-1 items-center justify-center gap-x-5 2xl:gap-x-7 xl:flex">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="group relative">
                <button data-testid={`docstate-nav-${slug(item.label)}`} className="inline-flex items-center gap-1 whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-[#C9BBB2] transition-colors hover:text-[#F3E5AB]">
                  {item.label} <ChevronDown className="h-3 w-3" />
                </button>
                <div className="invisible absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100">
                  <div className="ds-card overflow-hidden p-1.5">
                    {item.children.map((c) => (
                      <NavLink key={c.label} to={c.to} data-testid={`docstate-nav-${slug(c.label)}`} className="block rounded-lg px-3 py-2.5 text-sm text-[#C9BBB2] transition-colors hover:bg-[#C9A24B]/10 hover:text-[#F3E5AB]">
                        {c.label}
                      </NavLink>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.label} to={item.to} data-testid={`docstate-nav-${slug(item.label)}`} className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-[0.12em] text-[#C9BBB2] transition-colors hover:text-[#F3E5AB]">
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="flex shrink-0 items-center gap-3">
          <Link to={dl("/shortlist")} data-testid="header-shortlist" aria-label="Saved properties" className="relative inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#C9A24B]/30 text-[#F3E5AB] transition-colors hover:bg-[#C9A24B]/10">
            <Heart className="h-4 w-4" />
            {items.length > 0 && <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-[#C9A24B] px-1 text-[10px] font-bold text-[#0B0B0C]" data-testid="shortlist-count">{items.length}</span>}
          </Link>
          <Link to={dl("/list-with-us")} data-testid="docstate-header-cta" className="ds-gold-btn hidden whitespace-nowrap px-5 py-2.5 text-xs md:inline-flex">List Your Property</Link>
          <button className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[#C9A24B]/30 text-[#F3E5AB] xl:hidden" onClick={() => setOpen((v) => !v)} data-testid="docstate-nav-toggle" aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-[#C9A24B]/15 bg-[#0B0B0C]/97 px-4 py-4 backdrop-blur-2xl xl:hidden" data-testid="docstate-mobile-nav">
          <div className="flex flex-col gap-1">
            {NAV.map((item) =>
              item.children ? (
                <div key={item.label} className="py-1">
                  <p className="px-3 pt-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#6E635B]">{item.label}</p>
                  {item.children.map((c) => (
                    <NavLink key={c.label} to={c.to} data-testid={`docstate-mnav-${slug(c.label)}`} className="block rounded-lg px-3 py-2.5 text-sm text-[#C9BBB2] hover:bg-white/5 hover:text-[#F3E5AB]">{c.label}</NavLink>
                  ))}
                </div>
              ) : (
                <NavLink key={item.label} to={item.to} data-testid={`docstate-mnav-${slug(item.label)}`} className="rounded-lg px-3 py-2.5 text-sm font-medium text-[#C9BBB2] hover:bg-white/5 hover:text-[#F3E5AB]">{item.label}</NavLink>
              )
            )}
            <Link to={dl("/list-with-us")} className="ds-gold-btn mt-3 px-5 py-3 text-sm">List Your Property</Link>
          </div>
        </div>
      )}
    </header>
  );
};
